import {
  Box,
  CircularProgress,
  CircularProgressProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import { useEffect, useState } from 'react';
import { useVigoToast } from 'src/@core/hooks/useVigoToast';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import { bulkAddMasterDataRows, getMasterDataTableRows } from 'src/services/masterdata';
import VigoDataGrid from '..';
import VigoSpinner from '../../vigo-spinner';
import { BulkImportModel, ImportedRowsPopupProps, LookupDetails } from '../datagrid-types';
import { VigoButton } from '../../vigo-button';
import { useVigoCamelCase } from 'src/@core/hooks/useVigoCamelCase';
import { generateModel } from '../helpers';

const ImportedRowsPopup = ({
  setOpen,
  setRowHeaders,
  setValues,
  setErrorMappingFile,
  popupDisplayHeaders,
  masterDataObject,
  values,
  endPoint,
  location,
  refresh,
  checkRowsForErrors,
  errorMappingFile,
  lookupColumns,
  open,
  fileTooBig,
  setFileTooBig
}: ImportedRowsPopupProps) => {
  // State
  const [loading, setLoading] = useState<boolean>(false);
  const [importing, setImporting] = useState<boolean>(false);
  const [importProgress, setImportProgress] = useState<number>(0);
  const [lookupData, setLookupData] = useState<LookupDetails[]>([]);
  const [editableDataGridColumns, setEditableDataGridColumns] = useState<any[]>([]);
  const [rowsFromCSV, setRowsFromCSV] = useState<any[]>([]);
  const [importButtonDisabled, setImportButtonDisabled] = useState<boolean>(false);
  const [rowsToCheck, setRowsToCheck] = useState<any[] | undefined>();
  const [showErrorPrompt, setShowErrorPrompt] = useState<boolean>(false);

  // Hooks
  const { t } = useVigoTranslation();
  const { vigoToast } = useVigoToast({});
  const { toCamelCase } = useVigoCamelCase();

  const handleCloseDialog = () => {
    setOpen(false);
    setFileTooBig?.(false);
    setRowHeaders([]);
    setValues({});
    setErrorMappingFile?.(false);
  };

  const getEditableDataGridColumns = () => {
    const masterDataColumns: any[] = [];

    // Add Editable Data Grid column headers
    for (let index = 0; index < popupDisplayHeaders.length; index++) {
      // Custom Columns
      if (popupDisplayHeaders[index] == 'importOrUpdate' || popupDisplayHeaders[index] == 'importRow') {
        const col = popupDisplayHeaders[index];
        masterDataColumns.push(col);
      }
      if (popupDisplayHeaders[index] == 'hasError' || popupDisplayHeaders[index] == 'errorList') {
        continue;
      }

      // MasterData Columns
      const column = masterDataObject?.masterDataColumns?.find(x => toCamelCase(x.name) == popupDisplayHeaders[index]);
      if (column) {
        masterDataColumns.push(column);
      }
    }

    setEditableDataGridColumns(masterDataColumns);
  };

  const anyMappingErrors = (rows?: any[]): boolean => {
    if (rows) {
      for (let index = 0; index < rows.length; index++) {
        const row = rows[index];
        if (row.hasError == true) {
          setImportButtonDisabled(true);
          setValues({
            totalItemCount: rows.length,
            pagedItems: rows.length > 25 ? rows.slice(0, 25) : rows,
            allItems: rows
          });

          return true;
        }
      }

      setImportButtonDisabled(false);
      setValues({
        totalItemCount: rows.length,
        pagedItems: rows.length > 25 ? rows.slice(0, 25) : rows,
        allItems: rows
      });

      return false;
    }

    if (values.allItems) {
      for (let index = 0; index < values.allItems.length; index++) {
        const row = values.allItems[index];
        if (row.hasError == true) {
          //console.log('error row', row);

          return true;
        }
      }
    }

    return false;
  };

  function chunkRequests(array: any[], size: number) {
    const result = [];
    let pos = 0;
    while (pos < array.length) {
      result.push(array.slice(pos, pos + size));
      pos += size;
    }

    return result;
  }

  const mapImportRows = async () => {
    setImporting(true);

    const requestData: BulkImportModel[] = [];

    await getMasterDataTableRows({
      endPoint: endPoint,
      serviceLocation: location
    })
      .then(async existingRows => {
        for (let index = 0; index < rowsFromCSV.length; index++) {
          const row = rowsFromCSV[index];
          // ignore row from import if importRow is false
          if (row.importRow == false) {
            continue;
          }

          let id;
          let version;
          const hasCode = rowsFromCSV[index].code != undefined;
          const hasName = rowsFromCSV[index].name != undefined;

          // Check if existing row in table already - prepare for update instead of create
          if (hasCode) {
            if (existingRows.data.find((x: { code: string }) => x.code == rowsFromCSV[index].code)) {
              id = existingRows.data.find((x: { code: string }) => x.code == rowsFromCSV[index].code).id;
              version = existingRows.data.find((x: { code: string }) => x.code == rowsFromCSV[index].code).version;
            }
          } else if (hasName) {
            if (existingRows.data.find((x: { name: string }) => x.name == rowsFromCSV[index].name)) {
              id = existingRows.data.find((x: { name: string }) => x.name == rowsFromCSV[index].name).id;
              version = existingRows.data.find((x: { name: string }) => x.name == rowsFromCSV[index].name).version;
            }
          } else {
            id = undefined;
          }

          const model = generateModel(popupDisplayHeaders, toCamelCase, lookupData, row, id, version, masterDataObject);

          requestData.push({ id: id, model: model });
        }

        // Chunk requests to server
        const chunkedRequests = chunkRequests(requestData, 20);
        const newRows: any[] = [];

        for (let index = 0; index < chunkedRequests.length; index++) {
          const chunkedRequestData = chunkedRequests[index];

          await bulkAddMasterDataRows({
            data: chunkedRequestData,
            serviceLocation: location,
            endPoint: endPoint
          })
            .then(res => {
              for (let chunkedDataIndex = 0; chunkedDataIndex < res.data.data.length; chunkedDataIndex++) {
                newRows.push(res.data.data[chunkedDataIndex]);
              }

              setImportProgress(previousProgress =>
                previousProgress >= 100 ? 100 : (newRows.length / requestData.length) * 100
              );
            })
            .catch(err => {
              vigoToast.error(err.message);

              return;
            });
        }

        vigoToast.success(newRows.length + ' ' + t('rows successfully imported!'));
        refresh?.(newRows);
        setOpen(false);
        setRowHeaders([]);
        setValues({});
        setImporting(false);
      })
      .catch(err => {
        vigoToast.error(err.message);
        setRowHeaders([]);
        setValues({});
        setImporting(false);

        return;
      });
  };

  useEffect(() => {
    if (rowsToCheck) {
      const rows = checkRowsForErrors(rowsToCheck ?? [], toCamelCase, lookupColumns, t, masterDataObject);
      anyMappingErrors(rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsToCheck]);

  useEffect(() => {
    setLoading(true);

    if (errorMappingFile == true) {
      setLoading(false);
      setShowErrorPrompt(true);

      return;
    }

    getEditableDataGridColumns();
    const errors = anyMappingErrors();

    if (errors) {
      setImportButtonDisabled(true);
    }

    setLookupData?.(lookupColumns ?? []);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ImportProgress = function (props: CircularProgressProps & { value: number }) {
    const { value } = props;

    return (
      <>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress size={100} variant="determinate" color="success" thickness={2} {...props} />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <Typography variant="caption" sx={{ fontSize: 20 }}>{`${Math.round(value)}%`}</Typography>
          </Box>
        </Box>
        <VigoTypography value="This may take some time to complete - please wait" sx={{ fontSize: 15 }} />
        <br />
        <br />
        <VigoSpinner customSx={{ height: '10vh' }} name="importedRowsSpinner 1" />
      </>
    );
  };

  return (
    <>
      {showErrorPrompt && (
        <Dialog fullWidth open={open} keepMounted={false} aria-labelledby="errormapping-dialog-title">
          <>
            <DialogTitle id="errormapping-dialog-title">
              <div>
                <VigoTypography value="Bad File Detected" variant="h5" align="center" />
              </div>
            </DialogTitle>
            <DialogContent
              sx={{
                mt: '5px'
              }}>
              <VigoTypography value="Unexpected columns detected in your import file, are you sure it is the correct file?" />
            </DialogContent>
            <DialogActions>
              <VigoButton
                name="close"
                variant="outlined"
                value="Close"
                size="large"
                color="secondary"
                onClick={handleCloseDialog}
              />
            </DialogActions>
          </>
        </Dialog>
      )}
      {!showErrorPrompt && (
        <Dialog
          fullWidth
          maxWidth={importing ? 'sm' : 'xl'}
          open={open}
          keepMounted={false}
          aria-labelledby="importrows-dialog-title">
          <DialogTitle id="importrows-dialog-title">
            <div>
              <VigoTypography value={!importing ? 'Imported Rows' : 'Importing...'} variant="h5" align="center" />
            </div>
          </DialogTitle>
          <DialogContent sx={{ mt: '5px', maxHeight: '610px' }}>
            {!importing && (
              <Box
                sx={{
                  border: '1px solid #d3d3d3',
                  borderRadius: '4px',
                  flex: 1,
                  flexBasis: '250px',
                  flexShrink: 0,
                  width: '100%'
                }}>
                <VigoDataGrid
                  name={'ImportedRows'}
                  editableColumns={editableDataGridColumns}
                  currentLoadedRows={values.pagedItems?.length ?? 0}
                  totalRows={values.totalItemCount ?? 0}
                  columns={[]}
                  customActions={[]}
                  initialState={{ pinnedColumns: { left: ['importOrUpdate'], right: ['actions'] } }}
                  dataSource={values.pagedItems ?? []}
                  fullDataSource={values.allItems ?? []}
                  isEditable
                  disableExport
                  disableImport
                  disableSearch
                  disableFilters
                  disablePrint
                  disableLayoutReset
                  sx={{ height: '60vh' }}
                  editMode="row"
                  wrapHeader
                  density="standard"
                  {...{ setRowsToCheck, setRowsFromCSV, lookupColumns, masterDataObject }}
                />
              </Box>
            )}

            {importing && (
              <div style={{ textAlign: 'center' }}>
                <ImportProgress value={importProgress} />
              </div>
            )}
          </DialogContent>
          {importButtonDisabled && !importing && (
            <VigoTypography
              value="There are outstanding errors with your import data, you will not be able to import until these are fixed."
              sx={{ color: 'red' }}
              variant="h5"
              align="center"
            />
          )}
          {!importButtonDisabled && !importing && (
            <VigoTypography value="Are you sure you want to import these rows?" variant="h5" align="center" />
          )}
          {!importing && (
            <DialogActions>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <VigoButton
                  name="yes"
                  value="Yes"
                  variant="contained"
                  size="large"
                  sx={{ mr: 3 }}
                  onClick={mapImportRows}
                  disabled={importButtonDisabled}
                />
                <VigoButton
                  name="no"
                  value="No"
                  variant="outlined"
                  size="large"
                  color="secondary"
                  onClick={handleCloseDialog}
                />
              </Box>
            </DialogActions>
          )}
        </Dialog>
      )}
      {loading && <VigoSpinner name="importedRowsLoadingSpinner" />}
      {fileTooBig && (
        <Dialog fullWidth open={fileTooBig} keepMounted={false} aria-labelledby="errormapping-dialog-title">
          <>
            <DialogTitle id="errormapping-dialog-title">
              <div>
                <VigoTypography value="Bad File Detected" variant="h5" align="center" />
              </div>
            </DialogTitle>
            <DialogContent
              sx={{
                mt: '5px'
              }}>
              <VigoTypography value="File is too big - max size 200 rows" sx={{ textAlign: 'center', fontSize: 15 }} />
            </DialogContent>
            <DialogActions>
              <VigoButton
                name="close"
                variant="outlined"
                value="Close"
                size="large"
                color="secondary"
                onClick={handleCloseDialog}
              />
            </DialogActions>
          </>
        </Dialog>
      )}
    </>
  );
};

export default ImportedRowsPopup;
